<template>
  <div class="review-root">
    <!-- Header -->
    <div class="review-header">
      <!-- <div class="header-toggle">
        <v-switch
          v-model="isGalleryView"
          inset
          dense
          hide-details
          label="Gallery View"
          color="#b8a4e8"
        />
      </div> -->
      <div class="header-brand">
        <div class="brand-dot"></div>
        <span class="header-title">Photo Review</span>
      </div>

      <div class="header-progress" v-if="submissions.length">
        <span class="progress-label"
          >{{ currentIndex }} of {{ submissions.length }} done</span
        >
        <div class="progress-track">
          <div
            class="progress-fill"
            :style="{ width: progressPercent + '%' }"
          ></div>
        </div>
      </div>

      <div class="header-keys">
        <span class="key-hint"><kbd>←</kbd> Reject</span>
        <span class="key-hint"><kbd>→</kbd> Approve</span>
      </div>
    </div>

    <!-- Body -->
    <div>
      <div class="review-body">
        <!-- GALLERY VIEW -->
        <v-container v-if="isGalleryView" fluid class="pa-0">
          <!-- Bulk Action Bar -->
          <v-row class="mb-4 align-center" no-gutters>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-checkbox
                v-model="isAllSelected"
                :indeterminate="selectedIds.length > 0 && !isAllSelected"
                label="Select All"
                @change="toggleSelectAll"
                hide-details
                dense
              />
              <span class="ml-3 grey--text text--darken-1">
                {{ selectedIds.length }} selected
              </span>
            </v-col>

            <v-col cols="12" md="6" class="text-md-right mt-2 mt-md-0">
              <v-btn
                color="green"
                class="mr-2"
                :disabled="!selectedIds.length || isBulkProcessing"
                @click="bulkUpdateStatus('APPROVED')"
              >
                Approve Selected
              </v-btn>

              <v-btn
                color="red"
                dark
                :disabled="!selectedIds.length || isBulkProcessing"
                @click="bulkUpdateStatus('REJECTED')"
              >
                Reject Selected
              </v-btn>
            </v-col>
          </v-row>

          <v-row class="mb-2" no-gutters>
            <v-col cols="12" class="d-flex justify-end">
              <v-switch
                v-model="groupByUser"
                inset
                dense
                hide-details
                label="Group by user"
                color="#7a6e9a"
              />
            </v-col>
          </v-row>

          <!-- Gallery Grid -->
          <v-row>
            <template v-for="group in galleryGroups">
              <v-col
                v-if="groupByUser"
                :key="'header-' + group.userId"
                cols="12"
                class="user-group-header-wrap"
              >
                <div class="user-group-header">
                  <div class="user-group-id">
                    <v-icon small color="#7a6e9a" class="mr-1"
                      >mdi-account-circle-outline</v-icon
                    >
                    {{ group.userId }}
                  </div>
                  <div class="user-group-count">{{ group.items.length }} items</div>
                </div>
              </v-col>

              <v-col
                v-for="item in group.items"
                :key="item._id"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card
                  elevation="6"
                  class="gallery-card"
                  :class="{ 'duplicate-highlight': item.isDuplicate }"
                >
                  <!-- Checkbox -->
                  <v-checkbox
                    v-model="selectedIds"
                    :value="item._id"
                    hide-details
                    dense
                    class="gallery-checkbox"
                  />

                  <v-img
                    :src="item.imageUrl"
                    height="200"
                    cover
                    class="gallery-image"
                    @click="openImagePreview(item.imageUrl)"
                    style="cursor: pointer"
                  >
                    <div v-if="item.isDuplicate" class="duplicate-pill">
                      <v-icon small class="mr-1">mdi-content-duplicate</v-icon>
                      Duplicate
                    </div>
                    <button
                      v-if="item.isDuplicate"
                      class="duplicate-original-link"
                      :disabled="!item.duplicateOf || originalLookupId !== null"
                      @click.stop="openOriginalFromDuplicate(item)"
                    >
                      <v-icon x-small class="mr-1">mdi-image-search-outline</v-icon>
                      {{
                        originalLookupId === item._id
                          ? "Loading original..."
                          : "View original"
                      }}
                    </button>
                  </v-img>

                  <v-card-actions class="justify-space-between align-center">
                    <div class="gallery-left-actions">
                      <div
                        v-if="item.aiClassification"
                        class="gallery-ai-badge"
                        :class="'badge-' + item.aiClassification.toLowerCase()"
                      >
                        <v-icon :size="14">{{
                          getAiClassificationIcon(item.aiClassification)
                        }}</v-icon>
                        <span class="badge-text-small">{{
                          item.aiClassification
                        }}</span>
                      </div>
                    </div>

                    <v-btn
                      icon
                      color="red"
                      @click="updateStatusFromGallery(item._id, 'REJECTED')"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>

                    <v-btn
                      icon
                      color="green"
                      @click="updateStatusFromGallery(item._id, 'APPROVED')"
                    >
                      <v-icon>mdi-check</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      color="orange"
                      @click="updateStatusFromGallery(item._id, 'DUPLICATE')"
                      :disabled="isProcessing"
                    >
                      <v-icon>mdi-content-duplicate</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </template>
          </v-row>
        </v-container>

        <!-- SWIPE VIEW -->
        <div class="card-body" v-else>
          <transition name="card-pop" mode="out-in">
            <div
              v-if="currentSubmission"
              :key="currentIndex"
              class="card-scene"
            >
              <!-- Stack ghost cards for depth -->
              <div class="card-ghost card-ghost--2"></div>
              <div class="card-ghost card-ghost--1"></div>

              <!-- Main Card -->
              <div
                class="submission-card"
                v-hammer:swipe="onSwipe"
                :class="cardTiltClass"
              >
                <!-- Approve / Reject tint overlays -->
                <div
                  class="tint-overlay tint-reject"
                  :class="{ visible: swipeHint === 'reject' }"
                ></div>
                <div
                  class="tint-overlay tint-approve"
                  :class="{ visible: swipeHint === 'approve' }"
                ></div>

                <!-- Image -->
                <div
                  class="image-frame"
                  :class="{ 'duplicate-highlight': currentSubmission.isDuplicate }"
                >
                  <v-img
                    :src="currentSubmission.imageUrl"
                    height="340"
                    cover
                    class="submission-image"
                  >
                    <div
                      v-if="currentSubmission.isDuplicate"
                      class="duplicate-pill duplicate-pill-lg"
                    >
                      <v-icon small class="mr-1">mdi-content-duplicate</v-icon>
                      Duplicate
                    </div>
                    <template v-slot:placeholder>
                      <div class="image-loading">
                        <v-progress-circular
                          indeterminate
                          color="#b8a4e8"
                          size="32"
                        />
                      </div>
                    </template>
                  </v-img>
                  <div class="remaining-pill">
                    {{ submissions.length - currentIndex }} left
                  </div>
                  <button
                    v-if="currentSubmission.isDuplicate"
                    class="duplicate-original-link duplicate-original-link-lg"
                    :disabled="
                      !currentSubmission.duplicateOf || originalLookupId !== null
                    "
                    @click.stop="openOriginalFromDuplicate(currentSubmission)"
                  >
                    <v-icon x-small class="mr-1">mdi-image-search-outline</v-icon>
                    {{
                      originalLookupId === currentSubmission._id
                        ? "Loading original..."
                        : "View original image"
                    }}
                  </button>
                </div>

                <!-- Meta -->
                <div class="card-meta">
                  <div class="meta-row">
                    <div class="meta-item">
                      <span class="meta-label">User ID</span>
                      <span class="meta-value mono">{{
                        currentSubmission.userId
                      }}</span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Submitted</span>
                      <span class="meta-value">{{
                        formatDate(currentSubmission.createdAt)
                      }}</span>
                    </div>
                  </div>
                  <div class="meta-row">
                    <div class="meta-item">
                      <span class="meta-label">📍 Latitude</span>
                      <span class="meta-value mono coord">{{
                        currentSubmission.location.coordinates[1].toFixed(5)
                      }}</span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">📍 Longitude</span>
                      <span class="meta-value mono coord">{{
                        currentSubmission.location.coordinates[0].toFixed(5)
                      }}</span>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Action Buttons -->
              <div class="action-row">
                <button
                  class="action-btn reject-btn"
                  @click="rejectSubmission"
                  :disabled="isProcessing"
                >
                  <span class="btn-icon">✕</span>
                  <span class="btn-label">Reject</span>
                </button>

                <div class="swipe-hint-tip">swipe or use keys</div>

                <button
                  class="action-btn approve-btn"
                  @click="approveSubmission"
                  :disabled="isProcessing"
                >
                  <span class="btn-icon">✓</span>
                  <span class="btn-label">Approve</span>
                </button>
              </div>
            </div>
          </transition>

          <!-- Empty State -->
          <transition name="fade">
            <div v-if="!currentSubmission && !isLoading" class="empty-state">
              <div class="empty-blob">🎉</div>
              <div class="empty-title">All done!</div>
              <div class="empty-sub">
                No more submissions waiting for review.
              </div>
              <button class="refresh-btn" @click="getAllSubmissions">
                Check for more
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <v-dialog
      v-model="isImagePreviewOpen"
      fullscreen
      content-class="image-preview-dialog"
    >
      <v-card flat class="preview-fullscreen">
        <!-- Close Button -->
        <v-btn
          icon
          class="preview-close-btn"
          @click="closeImagePreview"
        >
          <v-icon color="white" size="34">mdi-close</v-icon>
        </v-btn>
        <div v-if="previewImageLabel" class="preview-image-label">
          {{ previewImageLabel }}
        </div>

        <!-- Image -->
        <v-img :src="previewImageUrl" contain class="preview-image" />
      </v-card>
    </v-dialog>

    <!-- AI Reason Dialog -->
    <v-dialog
      v-model="isAiReasonDialogOpen"
      max-width="600"
      persistent
    >
      <v-card class="ai-reason-dialog">
        <!-- Header -->
        <v-card-title class="ai-reason-header">
          <v-icon color="#d95f5f" class="mr-2">mdi-alert-circle</v-icon>
          AI Classification Reason
        </v-card-title>

        <!-- Content -->
        <v-card-text class="ai-reason-content">
          <div class="reason-box">
            <p class="reason-text">{{ currentAiReason }}</p>
          </div>
          <div class="reason-info">
            <v-icon small>mdi-information-outline</v-icon>
            <span>The AI has flagged this image for rejection. Please review the reason above.</span>
          </div>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions class="ai-reason-actions">
          <v-btn
            text
            color="#7a6e9a"
            @click="isAiReasonDialogOpen = false"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#d95f5f"
            dark
            @click="confirmRejectWithReason"
          >
            Reject Anyway
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2.js";
import moment from "moment";

const service = new RestResource();

export default {
  data() {
    return {
      submissions: [],
      currentIndex: 0,
      isProcessing: false,
      isLoading: false,
      swipeHint: null,
      isGalleryView: true,
      selectedIds: [],
      isBulkProcessing: false,
      isImagePreviewOpen: false,
      previewImageUrl: null,
      previewImageLabel: "",
      isAiReasonDialogOpen: false,
      currentAiReason: null,
      pendingRejectId: null,
      groupByUser: true,
      originalLookupId: null,
    };
  },

  computed: {
    currentSubmission() {
      return this.submissions[this.currentIndex];
    },
    progressPercent() {
      if (!this.submissions.length) return 0;
      return (this.currentIndex / this.submissions.length) * 100;
    },
    cardTiltClass() {
      if (this.swipeHint === "reject") return "tilt-left";
      if (this.swipeHint === "approve") return "tilt-right";
      return "";
    },
    isAllSelected() {
      return (
        this.submissions.length > 0 &&
        this.selectedIds.length === this.submissions.length
      );
    },
    galleryGroups() {
      if (!this.groupByUser) {
        return [
          {
            userId: "all",
            items: this.submissions,
          },
        ];
      }

      const grouped = this.submissions.reduce((acc, submission) => {
        const userId = submission.userId || "Unknown User";
        if (!acc[userId]) acc[userId] = [];
        acc[userId].push(submission);
        return acc;
      }, {});

      return Object.entries(grouped)
        .map(([userId, items]) => ({ userId, items }))
        .sort((a, b) => b.items.length - a.items.length);
    },
  },

  mounted() {
    this.isGalleryView = true;
    this.getAllSubmissions();
    window.addEventListener("keydown", this.handleKeydown);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
  },

  methods: {
    async getAllSubmissions() {
      this.isLoading = true;
      this.$setLoader();
      try {
        const res = await service.getAllOuternetSubmissions({
          status: "IN REVIEW",
        });
        this.submissions = res.data.data;
        this.currentIndex = 0;
      } finally {
        this.$disableLoader();
        this.isLoading = false;
      }
    },

    openImagePreview(url, label = "") {
      this.previewImageUrl = url;
      this.previewImageLabel = label;
      this.isImagePreviewOpen = true;
    },

    closeImagePreview() {
      this.isImagePreviewOpen = false;
      this.previewImageLabel = "";
    },

    normalizeSubmissionFromResponse(res) {
      const body = res && res.data;
      if (!body || typeof body !== "object") return null;
      if (body.data && typeof body.data === "object" && !Array.isArray(body.data))
        return body.data;
      if (Array.isArray(body.data) && body.data.length) return body.data[0];
      if (body.submission && typeof body.submission === "object")
        return body.submission;
      return body;
    },

    async openOriginalFromDuplicate(submission) {
      if (!submission?.duplicateOf || this.originalLookupId) return;

      this.originalLookupId = submission._id;
      this.$setLoader();
      try {
        const res = await service.getSubmissionById({
          submissionId: submission.duplicateOf,
        });
        const original = this.normalizeSubmissionFromResponse(res);
        const originalImage = original?.imageUrl;
        if (!originalImage) throw new Error("Original image not found");
        this.openImagePreview(originalImage, "Original image");
      } catch (err) {
        console.error("Failed to fetch original submission:", err);
      } finally {
        this.originalLookupId = null;
        this.$disableLoader();
      }
    },

    formatDate(date) {
      return moment(date).format("DD MMM YYYY · HH:mm");
    },

    getAiClassificationIcon(status) {
      const iconMap = {
        APPROVE: "mdi-check-circle",
        approve: "mdi-check-circle",
        REJECT: "mdi-close-circle",
        reject: "mdi-close-circle",
        PENDING: "mdi-clock-outline",
        pending: "mdi-clock-outline",
      };
      return iconMap[status] || "mdi-help-circle";
    },


    showAiReasonDialog(reason) {
      this.currentAiReason = reason;
      this.isAiReasonDialogOpen = true;
    },

    async confirmRejectWithReason() {
      this.isAiReasonDialogOpen = false;
      
      // Check if this is from gallery view
      if (this.pendingRejectId) {
        await this.executeGalleryStatusUpdate(this.pendingRejectId, "REJECTED");
        this.pendingRejectId = null;
      } else {
        // From swipe view
        await this.updateStatus("REJECTED");
      }
    },

    async approveSubmission() {
      this.swipeHint = "approve";
      setTimeout(() => (this.swipeHint = null), 320);
      await this.updateStatus("APPROVED");
    },

    async rejectSubmission() {
      this.swipeHint = "reject";
      setTimeout(() => (this.swipeHint = null), 320);
      
      // Check if aiReason exists and show dialog
      if (this.currentSubmission?.aiReason) {
        this.showAiReasonDialog(this.currentSubmission.aiReason);
      } else {
        await this.updateStatus("REJECTED");
      }
    },

    async updateStatus(status) {
      const submission = this.currentSubmission;
      if (!submission || this.isProcessing) return;

      this.isProcessing = true;
      this.$setLoader();

      try {
        await service.updateSubmissionStatus(submission._id, { status });
        this.currentIndex++;
      } catch (err) {
        console.error(err);
      } finally {
        this.isProcessing = false;
        this.$disableLoader();
      }
    },

    handleKeydown(e) {
      if (e.key === "ArrowLeft") this.rejectSubmission();
      if (e.key === "ArrowRight") this.approveSubmission();
    },

    onSwipe(event) {
      if (event.direction === 2) this.rejectSubmission();
      if (event.direction === 4) this.approveSubmission();
    },

    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedIds = [];
      } else {
        this.selectedIds = this.submissions.map((s) => s._id);
      }
    },

    async bulkUpdateStatus(status) {
      if (!this.selectedIds.length) return;

      this.isBulkProcessing = true;
      this.$setLoader();

      try {
        const dataToSend = {
          submissionIds: this.selectedIds,
          status: status,
        };

        await service.updateSubmissionStatus(dataToSend);

        this.submissions = this.submissions.filter(
          (item) => !this.selectedIds.includes(item._id),
        );

        this.selectedIds = [];
      } catch (err) {
        console.error(err);
      } finally {
        this.isBulkProcessing = false;
        this.$disableLoader();
      }
    },

    async updateStatusFromGallery(id, status) {
      // Check if rejecting and aiReason exists
      if (status === "REJECTED") {
        const submission = this.submissions.find((s) => s._id === id);
        if (submission?.aiReason) {
          this.currentAiReason = submission.aiReason;
          this.isAiReasonDialogOpen = true;
          this.pendingRejectId = id; // Store the ID for later use
          return;
        }
      }

      // Proceed with update
      await this.executeGalleryStatusUpdate(id, status);
    },

    async executeGalleryStatusUpdate(id, status) {
      this.$setLoader();

      try {
        const dataToSend = {
          submissionId: id,
          status: status,
        };

        await service.updateSubmissionStatus(dataToSend);

        this.submissions = this.submissions.filter((s) => s._id !== id);
        this.selectedIds = this.selectedIds.filter((sel) => sel !== id);
      } catch (err) {
        console.error(err);
      } finally {
        this.$disableLoader();
      }
    },
  },
};
</script>

<style scoped>
/* ─── Root ──────────────────────────────────────────── */
.review-root {
  min-height: 100vh;
  background: linear-gradient(145deg, #f3eeff 0%, #fef5fb 40%, #eaf8f3 100%);
  font-family: "Nunito", sans-serif;
  display: flex;
  flex-direction: column;
}

/* ─── Header ────────────────────────────────────────── */
.review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #ede8f5;
  gap: 20px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b8a4e8, #7dd9be);
  box-shadow: 0 0 0 3px #e8e0f7;
}

.header-title {
  font-size: 15px;
  font-weight: 800;
  color: #3a3250;
  letter-spacing: -0.01em;
}

.header-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1;
  max-width: 220px;
}

.progress-label {
  font-size: 11px;
  font-weight: 600;
  color: #7a6e9a;
}

.progress-track {
  width: 100%;
  height: 6px;
  background: #e8e0f7;
  border-radius: 99px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #b8a4e8, #7dd9be);
  border-radius: 99px;
  transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.header-keys {
  display: flex;
  gap: 12px;
}

.key-hint {
  font-size: 11px;
  font-weight: 600;
  color: #b0a8c8;
  display: flex;
  align-items: center;
  gap: 5px;
}

kbd {
  font-family: "Fira Code", monospace;
  font-size: 10px;
  background: #e8e0f7;
  border: 1px solid #b8a4e8;
  border-bottom-width: 2px;
  border-radius: 6px;
  padding: 2px 7px;
  color: #7c5cbf;
}

/* ─── Body ──────────────────────────────────────────── */
.review-body {
  flex: 1;
  padding: 48px 20px;
}

.card-body {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ─── Card Scene ────────────────────────────────────── */
.card-scene {
  width: 400px;
  max-width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.card-ghost {
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 24px;
  background: white;
  border: 1px solid #ede8f5;
}

.card-ghost--2 {
  top: -14px;
  height: 40px;
  transform: scale(0.9);
  opacity: 0.4;
  z-index: 0;
}

.card-ghost--1 {
  top: -8px;
  height: 40px;
  transform: scale(0.95);
  opacity: 0.65;
  z-index: 1;
}

/* ─── Main Card ─────────────────────────────────────── */
.submission-card {
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid #ede8f5;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(124, 92, 191, 0.06),
    0 12px 32px rgba(124, 92, 191, 0.1), 0 2px 0 #ede8f5;
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  z-index: 2;
}

.submission-card.tilt-left {
  transform: rotate(-4deg) translateX(-6px);
}
.submission-card.tilt-right {
  transform: rotate(4deg) translateX(6px);
}

.tint-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  border-radius: 24px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.tint-overlay.visible {
  opacity: 1;
}
.tint-reject {
  background: rgba(217, 95, 95, 0.16);
}
.tint-approve {
  background: rgba(125, 217, 190, 0.2);
}

/* ─── Image ─────────────────────────────────────────── */
.image-frame {
  position: relative;
  background: #e8e0f7;
}

.submission-image {
  display: block;
}

.image-loading {
  height: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8e0f7;
}

.remaining-pill {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(6px);
  border: 1px solid #ede8f5;
  border-radius: 99px;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #7a6e9a;
}

/* ─── Meta ──────────────────────────────────────────── */
.card-meta {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.meta-row {
  display: flex;
  gap: 10px;
}

.meta-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: #fdfaf6;
  border-radius: 12px;
  padding: 10px 12px;
  border: 1px solid #ede8f5;
}

.meta-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #b0a8c8;
}

.meta-value {
  font-size: 13px;
  font-weight: 600;
  color: #3a3250;
  word-break: break-all;
}

.meta-value.mono {
  font-family: "Fira Code", monospace;
  font-size: 11px;
}

.meta-value.coord {
  color: #7c5cbf;
}

/* ─── Actions ───────────────────────────────────────── */
.action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.action-btn {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-family: "Nunito", sans-serif;
  transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.18s ease;
}

.action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none !important;
}

.action-btn:not(:disabled):hover {
  transform: scale(1.12);
}
.action-btn:not(:disabled):active {
  transform: scale(0.94);
}

.reject-btn {
  background: #fde8e8;
  color: #d95f5f;
  box-shadow: 0 4px 14px rgba(217, 95, 95, 0.18);
}

.reject-btn:not(:disabled):hover {
  background: #fbd6d6;
  box-shadow: 0 6px 20px rgba(217, 95, 95, 0.3);
}

.approve-btn {
  background: #d4f5ec;
  color: #2e9e7e;
  box-shadow: 0 4px 14px rgba(125, 217, 190, 0.22);
}

.approve-btn:not(:disabled):hover {
  background: #bff0e2;
  box-shadow: 0 6px 20px rgba(125, 217, 190, 0.36);
}

.btn-icon {
  font-size: 22px;
  line-height: 1;
  font-weight: 800;
}
.btn-label {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.swipe-hint-tip {
  flex: 1;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #b0a8c8;
}

/* ─── Empty State ───────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.empty-blob {
  font-size: 52px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.empty-title {
  font-size: 26px;
  font-weight: 800;
  color: #3a3250;
}

.empty-sub {
  font-size: 14px;
  color: #7a6e9a;
  font-weight: 500;
}

.refresh-btn {
  margin-top: 8px;
  background: white;
  border: 1.5px solid #b8a4e8;
  color: #7c5cbf;
  font-family: "Nunito", sans-serif;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 24px;
  border-radius: 99px;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
  box-shadow: 0 2px 10px rgba(124, 92, 191, 0.12);
}

.refresh-btn:hover {
  background: #e8e0f7;
  transform: scale(1.04);
}

/* ─── Gallery Toggle ───────────────── */
.gallery-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #888;
  cursor: pointer;
}

.gallery-toggle input {
  accent-color: #b8a4e8;
}

/* ─── Gallery Grid ─────────────────── */
.gallery-view {
  width: 100%;
  max-width: 1200px;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.gallery-item {
  background: #141414;
  border: 1px solid #1e1e1e;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.6);
}

.gallery-actions {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
}

.mini-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
}

.reject-mini {
  background: rgba(255, 60, 60, 0.2);
  color: #ff5555;
  border: 1px solid rgba(255, 60, 60, 0.4);
}

.approve-mini {
  background: rgba(180, 150, 255, 0.2);
  color: #b8a4e8;
  border: 1px solid rgba(180, 150, 255, 0.4);
}

/* ─── Transitions ───────────────────────────────────── */
.card-pop-enter-active {
  transition: opacity 0.3s ease,
    transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.card-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.card-pop-enter {
  opacity: 0;
  transform: scale(0.93) translateY(14px);
}
.card-pop-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.gallery-card {
  position: relative;
}

.duplicate-highlight {
  box-shadow: 0 0 0 2px rgba(245, 124, 0, 0.45) inset;
}

.user-group-header-wrap {
  margin-top: 8px;
  margin-bottom: 4px;
}

.user-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid #e9e3f5;
  border-radius: 12px;
  padding: 10px 14px;
}

.user-group-id {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #5c4e80;
}

.user-group-count {
  font-size: 12px;
  font-weight: 700;
  color: #8f83ab;
}

.gallery-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 5;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  padding: 2px;
}

.preview-fullscreen {
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
  color: white;
  background: rgba(0, 0, 0, 0.5);
}

.preview-image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.gallery-image {
  transition: transform 0.2s ease;
}

.duplicate-pill {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #7a3e00;
  background: rgba(255, 193, 7, 0.92);
  border: 1px solid rgba(245, 124, 0, 0.5);
  border-radius: 999px;
  padding: 4px 10px;
  backdrop-filter: blur(2px);
}

.duplicate-pill-lg {
  font-size: 12px;
  padding: 6px 12px;
}

.gallery-left-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.duplicate-original-link {
  position: absolute;
  left: 10px;
  bottom: 10px;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(15, 23, 42, 0.65);
  color: #fff;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  backdrop-filter: blur(3px);
  transition: all 0.18s ease;
}

.duplicate-original-link:hover {
  background: rgba(30, 41, 59, 0.85);
}

.duplicate-original-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.duplicate-original-link-lg {
  left: 14px;
  bottom: 14px;
  font-size: 12px;
  padding: 6px 12px;
}

.gallery-image:hover {
  transform: scale(1.03);
}

.image-preview-dialog {
  background: rgba(0, 0, 0, 0.95);
}

.preview-fullscreen {
  height: 100vh;
  width: 100vw;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}

.preview-image {
  max-width: 100vw;
  max-height: 100vh;
}

.preview-image-label {
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 1000;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
}

.preview-close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 9999 !important;
  background: rgba(0, 0, 0, 0.6);
}

/* ─── AI Classification Badge ──────────────────────── */
.ai-classification-badge {
  margin: 12px 20px 0 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
}

.ai-classification-badge.badge-approve {
  background: rgba(125, 217, 190, 0.15);
  color: #2e9e7e;
  border: 1px solid rgba(125, 217, 190, 0.3);
}

.ai-classification-badge.badge-reject {
  background: rgba(217, 95, 95, 0.15);
  color: #d95f5f;
  border: 1px solid rgba(217, 95, 95, 0.3);
}

.ai-classification-badge.badge-pending {
  background: rgba(255, 193, 7, 0.15);
  color: #f57c00;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.badge-icon {
  display: flex;
  align-items: center;
}

.gallery-ai-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
}

.gallery-ai-badge.badge-approve {
  background: rgba(125, 217, 190, 0.2);
  color: #2e9e7e;
}

.gallery-ai-badge.badge-reject {
  background: rgba(217, 95, 95, 0.2);
  color: #d95f5f;
}

.gallery-ai-badge.badge-pending {
  background: rgba(255, 193, 7, 0.2);
  color: #f57c00;
}

.badge-text-small {
  font-size: 10px;
}

.flex-spacer {
  flex: 1;
}
</style>
